import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  SECTIONS, groupBySection, computeHiddenKeys,
  YN_TYPES, YN_CODES, isSpecifyShown, optionHasSpecify,
  NUMERIC_MAX, NUMERIC_SCALE, numericRangeError, branchNo,
} from "../Feasibility/FeasibilityEditor/data";
import { siteText } from "./siteText";

const SectionWrap = styled.div`
  margin-bottom: 28px;
`;

const SectionHeader = styled.div`
  background: #e9ecef;
  padding: 8px 14px;
  font-weight: 600;
  font-size: 14px;
  border: 1px solid #dee2e6;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
`;

// Fixed layout keeps the columns at the widths declared on the header row. Without it a
// long answer or option label widens the table past the page and the whole card scrolls
// sideways.
const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  padding: 8px 12px;
  background: #f1f3f5;
  border: 1px solid #dee2e6;
  text-align: left;
  font-size: 12px;
  color: #495057;
`;

const Td = styled.td`
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  vertical-align: top;
  overflow-wrap: anywhere;
  background: ${(p) => (p.$missing ? "#fff5f5" : "transparent")};
`;

// 번호도 문항 문구와 같이 한 단계 들여쓴다. 번호만 왼쪽에 붙어 있으면 계층이 문항 칸
// 에서만 일어나 두 칸이 어긋나 보인다. 들여쓰기가 있으니 정렬은 왼쪽으로 두어야 한다 —
// 가운데나 오른쪽에 붙이면 들여쓴 만큼이 도로 상쇄된다.
const NoTd = styled(Td)`
  width: 72px;
  white-space: nowrap;
  text-align: left;
  padding-left: ${(p) => (p.$sub ? 24 : 12)}px;
  color: #6c757d;
  border-left: ${(p) => (p.$missing ? "3px solid #dc3545" : "1px solid #dee2e6")};
`;

// 하위 문항의 문항 칸. 안내선은 셀의 border가 아니라 padding 안쪽에 그린다 —
// border-collapse 표에서 셀 테두리는 옆 칸과 공유하는 표선이라, 그걸 건드리면 계층이
// 아니라 표가 깨진 것처럼 보인다.
const SubTd = styled(Td)`
  padding-left: 30px;
  position: relative;
  &::before {
    content: "";
    position: absolute;
    left: 14px; top: 8px; bottom: 8px;
    border-left: 2px solid #dee2e6;
  }
`;

const RequiredMark = styled.span`
  color: #dc3545;
  margin-left: 4px;
  font-weight: 600;
`;

const MissingMsg = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #dc3545;
`;

// Sits under the input it rejects, in the answer column.
const FieldErrorMsg = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #dc3545;
`;

const YNWrap = styled.div`
  display: flex;
  gap: 8px;
`;

// N/A is deliberately neutral gray — it's neither the good nor the bad answer. Its
// background stays a shade darker than the unselected `transparent` so "selected" still
// reads at a glance.
const YN_COLORS = {
  Y:  { border: "#198754", bg: "#d1e7dd", fg: "#0f5132" },
  N:  { border: "#dc3545", bg: "#f8d7da", fg: "#842029" },
  NA: { border: "#6c757d", bg: "#dee2e6", fg: "#212529" },
};

const YNBtn = styled.button`
  padding: 5px 16px;
  border-radius: 4px;
  border: 1px solid ${(p) => (p.$selected ? YN_COLORS[p.$yn].border : "#dee2e6")};
  background: ${(p) => (p.$selected ? YN_COLORS[p.$yn].bg : "transparent")};
  color: ${(p) => (p.$selected ? YN_COLORS[p.$yn].fg : "#495057")};
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  font-size: 14px;
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};
`;

const SpecifyWrap = styled.div`
  margin-top: 8px;
  padding-left: 4px;
  border-left: 2px solid #dee2e6;
`;

const SpecifyLabel = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 4px;
`;

const Textarea = styled.textarea`
  width: 100%;
  max-width: 100%;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 14px;
  line-height: 1.4;
  resize: vertical;
  min-height: 64px;
  box-sizing: border-box;
  /* Long unbroken input (URLs, IDs) wraps instead of scrolling the box sideways. */
  overflow-wrap: anywhere;
  overflow-y: hidden;
  &:disabled { background: #f8f9fa; cursor: not-allowed; }
`;

// Free-text answers can run long, so the box grows with its content: the whole answer
// stays visible on multiple lines instead of hiding behind an inner scrollbar.
function AutoTextarea({ value, ...rest }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    // scrollHeight excludes borders, which box-sizing: border-box counts in `height`.
    const borders = el.offsetHeight - el.clientHeight;
    el.style.height = `${el.scrollHeight + borders}px`;
  }, [value]);

  return <Textarea ref={ref} value={value} {...rest} />;
}

const NumInput = styled.input`
  width: 140px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 14px;
  &:disabled { background: #f8f9fa; cursor: not-allowed; }
`;

// A number the column cannot hold must never reach the pending queue, so it lives in the
// box as a draft instead: the last storable value stays the answer, and the respondent
// sees why what they typed is not being kept. Leaving it uncommitted also means Submit
// still flags the question as unanswered, which is the honest state.
function NumericAnswerInput({ value, disabled, questionKey, lang, onCommit }) {
  const [draft, setDraft] = useState(null); // non-null only while the box holds a rejected value

  const error = draft == null ? "" : numericRangeError(draft, lang);
  const errorId = `numerr_${questionKey}`;

  function handleChange(e) {
    const raw = e.target.value;
    // Only the message is language-dependent; what counts as storable is not, so the
    // rejection itself reads the same either way.
    if (numericRangeError(raw)) {
      setDraft(raw);
      return;
    }
    setDraft(null);
    onCommit(raw === "" ? null : parseFloat(raw));
  }

  return (
    <>
      <NumInput
        type="number"
        min={-NUMERIC_MAX}
        max={NUMERIC_MAX}
        step={1 / 10 ** NUMERIC_SCALE}
        disabled={disabled}
        value={draft ?? value ?? ""}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        onChange={handleChange}
      />
      {error && <FieldErrorMsg id={errorId}>{error}</FieldErrorMsg>}
    </>
  );
}

const ChecklistWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CheckOptLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.$faded ? 0.35 : 1)};
`;

const CheckOptText = styled.span`
  font-size: 14px;
  line-height: 1.4;
  min-width: 0;
  overflow-wrap: anywhere;
  color: ${(p) => (p.$faded ? "#adb5bd" : "#212529")};
`;

const HelperText = styled.div`
  padding-left: 24px;
  font-size: 12px;
  color: #6c757d;
  margin-top: 1px;
`;

function parseJsonSafe(str, fallback) {
  try { return JSON.parse(str || ""); } catch { return fallback; }
}

export default function SiteQuestionForm({
  answers, onChange, disabled, questions, missingKeys, lang,
}) {
  // The chrome — column headings, section labels, Yes/No, the required notice — follows the
  // page's language. The question text and option labels are the study's own content and
  // are shown as authored, in whichever language they were written.
  const t = siteText(lang);

  function get(key) { return answers[key] || {}; }
  function set(key, field, value) {
    if (onChange) onChange(key, field, value);
  }

  function handleChecklistToggle(questionKey, optKey, options, singleSelect) {
    const ans = get(questionKey);
    let checked = parseJsonSafe(ans.answerText, []);
    let specMap = parseJsonSafe(ans.specifyText, {});
    const wasChecked = checked.includes(optKey);

    if (wasChecked) {
      checked = checked.filter((k) => k !== optKey);
      delete specMap[optKey];
    } else {
      if (singleSelect) {
        // uncheck all others, clear their specify
        for (const k of checked) delete specMap[k];
        checked = [optKey];
      } else {
        checked = [...checked, optKey];
      }
    }
    set(questionKey, "answerText", JSON.stringify(checked));
    set(questionKey, "specifyText", JSON.stringify(specMap));
  }

  function handleChecklistSpecify(questionKey, optKey, text) {
    const ans = get(questionKey);
    const specMap = { ...parseJsonSafe(ans.specifyText, {}), [optKey]: text };
    set(questionKey, "specifyText", JSON.stringify(specMap));
  }

  const sections = useMemo(
    () => (questions && questions.length ? groupBySection(questions) : SECTIONS),
    [questions]
  );
  const sectionKeys = Object.keys(sections).sort((a, b) => Number(a) - Number(b));


  const hiddenQuestionKeys = useMemo(
    () => computeHiddenKeys(sections, answers),
    [sections, answers]
  );

  return (
    <>
      {sectionKeys.map((sec) => {
        const { title, questions: qs } = sections[sec];
        return (
          <SectionWrap key={sec}>
            <SectionHeader>{t.section(sec, title)}</SectionHeader>
            <Table>
              <thead>
                <tr>
                  <Th style={{ width: 72 }}>{t.colNo}</Th>
                  <Th style={{ width: "40%" }}>{t.colQuestion}</Th>
                  <Th>{t.colAnswer}</Th>
                </tr>
              </thead>
              <tbody>
                {qs.map((q) => {
                  if (hiddenQuestionKeys.has(q.key)) return null;
                  const ans = get(q.key);
                  const isMissing = !!missingKeys && missingKeys.has(q.key);
                  const QTd = q.parentKey ? SubTd : Td;
                  return (
                    <tr key={q.key} id={`q_${q.key}`}>
                      <NoTd $missing={isMissing} $sub={!!q.parentKey}>{branchNo(q)}</NoTd>
                      <QTd $missing={isMissing}>
                        {q.text}
                        {q.isRequired !== false && <RequiredMark>*</RequiredMark>}
                        {isMissing && <MissingMsg>{t.requiredField}</MissingMsg>}
                      </QTd>
                      <Td $missing={isMissing}>
                        {YN_TYPES.includes(q.type) && (
                          <>
                            <YNWrap>
                              {YN_CODES[q.type].map((code) => (
                                <YNBtn
                                  key={code}
                                  type="button"
                                  $yn={code}
                                  $selected={ans.answerYN === code}
                                  $disabled={disabled}
                                  onClick={() => !disabled && set(q.key, "answerYN", code)}
                                >
                                  {t.yn[code] || code}
                                </YNBtn>
                              ))}
                            </YNWrap>
                            {isSpecifyShown(q, ans) && (
                              <SpecifyWrap>
                                {q.specifyLabel && <SpecifyLabel>{q.specifyLabel}</SpecifyLabel>}
                                <AutoTextarea
                                  disabled={disabled}
                                  value={ans.specifyText || ""}
                                  onChange={(e) => set(q.key, "specifyText", e.target.value)}
                                />
                              </SpecifyWrap>
                            )}
                          </>
                        )}
                        {q.type === "numeric" && (
                          <NumericAnswerInput
                            questionKey={q.key}
                            lang={lang}
                            disabled={disabled}
                            value={ans.answerNumeric}
                            onCommit={(num) => set(q.key, "answerNumeric", num)}
                          />
                        )}
                        {q.type === "text" && (
                          <AutoTextarea
                            disabled={disabled}
                            value={ans.answerText || ""}
                            onChange={(e) => set(q.key, "answerText", e.target.value)}
                          />
                        )}
                        {q.type === "checklist" && (() => {
                          const opts = q.options || [];
                          const checked = parseJsonSafe(ans.answerText, []);
                          const specMap = parseJsonSafe(ans.specifyText, {});
                          return (
                            <ChecklistWrap>
                              {opts.map((opt) => {
                                const isChecked = checked.includes(opt.key);
                                return (
                                  <div key={opt.key}>
                                    <CheckOptLabel $disabled={disabled}>
                                      <input
                                        type={q.singleSelect ? "radio" : "checkbox"}
                                        name={q.singleSelect ? `checklist_${q.key}` : undefined}
                                        checked={isChecked}
                                        disabled={disabled}
                                        onChange={() => handleChecklistToggle(q.key, opt.key, opts, q.singleSelect)}
                                        style={{ marginTop: 3, flexShrink: 0 }}
                                      />
                                      <CheckOptText>{opt.label}</CheckOptText>
                                    </CheckOptLabel>
                                    {/* Above the follow-up input and shown either way:
                                        the helper explains what the option means, which
                                        is as much use after checking it as before. */}
                                    {opt.helperText && (
                                      <HelperText>{opt.helperText}</HelperText>
                                    )}
                                    {isChecked && optionHasSpecify(opt) && (
                                      <SpecifyWrap>
                                        <AutoTextarea
                                          disabled={disabled}
                                          placeholder={opt.specifyLabel || ""}
                                          value={specMap[opt.key] || ""}
                                          onChange={(e) =>
                                            handleChecklistSpecify(q.key, opt.key, e.target.value)
                                          }
                                        />
                                      </SpecifyWrap>
                                    )}
                                  </div>
                                );
                              })}
                            </ChecklistWrap>
                          );
                        })()}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </SectionWrap>
        );
      })}
    </>
  );
}
